import { Injectable, computed, signal } from "@angular/core";
import { AUTH_ES } from "./auth.es";
import { AUTH_EN } from "./auth.en";

export type Lang = 'es' | 'en';

/**
 * El diccionario español es el contrato: define las claves, no los textos.
 * Si falta una clave en inglés (o sobra una que no existe en español), no compila.
 */
export type AuthKey = keyof typeof AUTH_ES;
export type AuthDictionary = Record<AuthKey, string>;

const STORAGE_KEY = 'dexcarga.lang';
const DICTIONARIES: Record<Lang, AuthDictionary> = { es: AUTH_ES, en: AUTH_EN };

/**
 * i18n de las pantallas de autenticación. El idioma vive en un signal, así que
 * cambiarlo repinta login, registro y recuperación sin recargar ni tocar el
 * router. Se persiste en localStorage para que la preferencia sobreviva a un
 * refresh o a volver más tarde.
 *
 * El resto de la app sigue en español: esto está acotado a `modules/auth`.
 */
@Injectable({ providedIn: 'root' })
export class I18nService {

  readonly lang = signal<Lang>(this.initialLang());
  readonly dictionary = computed(() => DICTIONARIES[this.lang()]);

  constructor() { this.syncDocumentLang(this.lang()); }

  setLang(lang: Lang) {
    this.lang.set(lang);
    this.syncDocumentLang(lang);
    try { localStorage.setItem(STORAGE_KEY, lang); } catch { /* modo privado / storage lleno */ }
  }

  /**
   * Traduce una clave. `params` interpola `{{nombre}}` — se usa poco, sólo donde
   * el texto lleva un dato del formulario.
   */
  t(key: AuthKey, params?: Record<string, string>): string {
    const text = this.dictionary()[key] ?? key;
    if (!params) return text;
    return Object.entries(params).reduce((acc, [name, value]) => acc.replaceAll(`{{${name}}}`, value), text);
  }

  /**
   * Manda lo que el usuario eligió la última vez. Sin preferencia guardada, el
   * idioma es español: dexCarga opera en México y olfatear `navigator.language`
   * hacía que un navegador en inglés abriera la app en inglés sin pedirlo.
   */
  private initialLang(): Lang {
    return this.readStored() ?? 'es';
  }

  private readStored(): Lang | null {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored === 'es' || stored === 'en' ? stored : null;
    } catch {
      return null;
    }
  }

  private syncDocumentLang(lang: Lang) {
    document.documentElement.lang = lang;
  }
}
